// Forms helper

import moment from 'moment';
import { caseFormConfig } from '@/Config/forms';
import { parseCamelCase, snakeToNormalCase } from './general';
import {integerRegex, floatRegex, emailRegex, phoneNumberRegex, engineSizeRegex} from './regex';

/**
 * Check if a form input is valid
 *
 * @param {object} formErrors state object containing form errors
 * @param {string} name the name of the input
 */
export const isFormFieldValid = (formErrors, name) => formErrors && formErrors[name];

/**
 * Show a small error message if an input is invalid
 *
 * @param {object} formErrors state object containing form errors
 * @param {string} name the name of the input
 */
export const getFormErrorMessage = (formErrors, name, centered) => {
    return isFormFieldValid(formErrors, name) && <small className={'p-error block mt-1 w-full' + (centered ? ' text-center' : '')}>{formErrors[name]}</small>;
};

/**
 * Get a field's label based on it's key
 *
 * @param {string} field the key of a field
 */
const getFieldLabel = (field) => {
    return field.search('_') > -1 ? snakeToNormalCase(field) : parseCamelCase(field);
};

/**
 * Validate form fields
 *
 * @param {array} validationRules array containing validation rules
 * @param {object} formData state object containing all of the form's data
 * @param {function} setFormErrors function which updates the state of the formErrors object
 */
export const validateForm = (validationRules, formData, setFormErrors, tab, isRuleset) => {
    let newFormErrors = {};
    for (let i = 0; i < validationRules.length; i++) {
        let item = validationRules[i],
            fieldName = isRuleset
                ? item['category'] + '[' + item['field'] + ']'
                : (tab
                    ? tab + '-' + item['field']
                    : item['field']
                ),
            field = isRuleset ? formData[item['category'] + '[' + item['field'] + ']'] : formData[item['field']],
            fieldLabel = getFieldLabel(item['field']);

        item['rules'].forEach((rule) => {
            let params = rule['params'];

            switch (rule['name']) {
                case 'required':
                    if (field === '' || field === null || field === undefined) {
                        newFormErrors[fieldName] = fieldLabel + ' is required.';
                    }
                    break;

                case 'minLength':
                    if (typeof (field) != 'undefined' && [...field].length < params['length']) {
                        newFormErrors[fieldName] = fieldLabel + ' minimum length is ' + params['length'] + '.';
                    }
                    break;

                case 'email':
                    if (field && !String(field).toLowerCase().match(emailRegex)) {
                        newFormErrors[fieldName] = 'Invalid email address.';
                    }
                    break;
                case 'integer':
                    if (field && !String(field).match(integerRegex)) {
                        newFormErrors[fieldName] = 'Field must contain a non-decimal number.';
                    }
                    break;

                case 'min':
                    if (typeof (field) != 'undefined' && field < params['value']) {
                        newFormErrors[fieldName] = `Field must contain a number greater or equal than ${params['value']}.`;
                    }
                    break;

                case 'max':
                    if (typeof (field) != 'undefined' && field > params['value']) {
                        newFormErrors[fieldName] = `Field must contain a number lower or equal than ${params['value']}.`;
                    }
                    break;

                case 'float':
                    if (field && !String(field).match(floatRegex)) {
                        newFormErrors[fieldName] = 'Field must contain a decimal number.';
                    }
                    break;

                case 'matchField':
                    if (field != formData[params['matchFieldName']]) {
                        newFormErrors[fieldName] = fieldLabel + ' does not match ' + getFieldLabel(params['matchFieldName']) + '.';
                    }
                    break;

                case 'dateSameOrBefore':
                    if (field && formData[params['matchFieldName']] && !moment(field).isSameOrBefore(moment(formData[params['matchFieldName']]))) {
                        newFormErrors[fieldName] = fieldLabel + ' must be the same or before ' + getFieldLabel(params['matchFieldName']) + '.';
                    }
                    break;

                case 'dateRange':
                    let notValid = false;

                    if (Array.isArray(field)) {
                        if (field.length < 2) {
                            notValid = true;
                        } else {
                            if (!moment.isDate(field[1])) {
                                notValid = true;
                            }
                        }
                    }

                    if (notValid) {
                        newFormErrors[fieldName] = 'End date is required.';
                    }
                    break;

                case 'phoneNumber':
                    if (field && !field.match(phoneNumberRegex)) {
                        newFormErrors[fieldName] = 'Invalid phone number.';
                    }
                    break;

                case 'engineSize':
                    if (field && !field.match(engineSizeRegex)) {
                        newFormErrors[fieldName] = 'Invalid engine size.';
                    }
                    break;

                default:
                    break;
            }
        });
    }

    setFormErrors(newFormErrors);

    return Object.keys(newFormErrors).length <= 0;
};

/**
 * Parse case fields data (in case of an audit log)
 *
 * @param {object} audit log data
 *
 * @return {array} fieldsData detailed info about the case fields
 */
export const mapFieldsData = (audit) => {
    let fieldsData = [];

    if (audit.type === 'update_case' && audit?.more_detail?.data && audit.more_detail?.relation) {
        let fieldsConfig = caseFormConfig.tabs.find((tab) => tab.key === audit.more_detail.relation)?.fields,
            allFields = [],
            data = audit.more_detail.data;

        fieldsConfig.forEach((config) => {
            allFields = allFields.concat(config.fields);
        });

        for (const key in data) {
            let fieldConfig = allFields.find((field) => field.name === key);

            if (fieldConfig) {
                let newField = {
                    name: fieldConfig?.label,
                    value: data[key]
                };

                // dates
                if (fieldConfig.type === 'date') {
                    let getDate = moment(data[key]);

                    if (getDate.isValid()) {
                        newField.value = getDate.format('DD.MM.YYYY HH:mm:ss');
                    }
                }

                // booleans
                if (fieldConfig.type === 'boolean') {
                    if (data[key] === false) {
                        newField.value = 'No';
                    }

                    if (data[key] === true) {
                        newField.value = 'Yes';
                    }
                }

                if (newField.value === null) {
                    newField.value = 'Not set';
                }

                fieldsData.push(newField);
            }
        }
    }

    return fieldsData;
};

/**
 *
 * @param relation
 * @returns {*[]}
 */
export const mapFieldsTable = (relation) => {
    let fieldsData = [];
    if (relation) {
        relation.forEach((field) => {
            let fieldsConfig = caseFormConfig.tabs.find((tab) => tab.key === field.relation)?.fields,
                allFields = [],
                data = field;

            fieldsConfig.forEach((config) => {
                allFields = allFields.concat(config.fields);
            });
            let fieldConfig = allFields.find((field) => field.name === data.field);
            if (fieldConfig) {
                let newField = {
                    name: fieldConfig?.label,
                    value: data.value,
                    type: data.type
                };

                // dates
                if (fieldConfig.type === 'date') {
                    let getDate = moment(data.value);

                    if (getDate.isValid()) {
                        newField.value = getDate.format('DD.MM.YYYY');
                    }
                }

                // booleans
                if (fieldConfig.type === 'boolean') {
                    if (data.value === false) {
                        newField.value = 'No';
                    }

                    if (data.value === true) {
                        newField.value = 'Yes';
                    }
                }

                if (newField.value === null) {
                    newField.value = 'Not set';
                }

                fieldsData = fieldsData.concat(newField);
            }
        });

    }
    return fieldsData;
};

/**
 * Get the config of a certain form field
 *
 * @param {string} category the category (db table) name
 * @param {string} fieldName the name of the field
 *
 * @returns {object|null} config info
 */
export const getFieldConfig = (category, fieldName) => {
    let fieldsConfig = caseFormConfig.tabs.find((tab) => tab.key == category)?.fields,
        allFields = [];

    if (fieldsConfig) {
        fieldsConfig.forEach((config) => {
            allFields = allFields.concat(config.fields);
        });
    }

    return allFields.length > 0 ? allFields.find((field) => field.name == fieldName) : null;
};
